/**
 * Imagor Image Transformation Service
 *
 * This service provides methods for transforming images using Imagor's URL-based API.
 * It supports resizing, cropping, and various other image transformations.
 *
 * USAGE EXAMPLES:
 *
 * import { imagorService } from '@/helpers';
 *
 * // Basic resize
 * const resizedUrl = imagorService.transformUrl(
 *   'https://img.dev.restomenu.cc/unsafe/1CTicNqJvHCa6KiDgvkUx7/1CWUvUBMGyYnLxdNW38L58.jpg',
 *   { width: 200, height: 200 }
 * );
 *
 * // With fit-in and smart detection
 * const transformedUrl = imagorService.transformUrl(imageUrl, {
 *   width: 300,
 *   height: 300,
 *   fitIn: true,
 *   smart: true
 * });
 *
 * // With alignment
 * const alignedUrl = imagorService.transformUrl(imageUrl, {
 *   width: 400,
 *   height: 300,
 *   halign: 'center',
 *   valign: 'middle'
 * });
 *
 * URL STRUCTURE:
 * https://domain.com/unsafe/fit-in/200x200/smart/path/to/image.jpg
 * │─────────────││──────││──────────────────────││──────────────│
 *    1 уровень     2 уровень     3 уровень (параметры)   4 уровень (путь)
 */

export interface ImagorTransformOptions {
  /** Width in pixels */
  width?: number;
  /** Height in pixels */
  height?: number;
  /** Use fit-in mode (image fits in specified dimensions without cropping) */
  fitIn?: boolean;
  /** Use smart detection of focal points */
  smart?: boolean;
  /** Horizontal alignment: left, right, or center */
  halign?: "left" | "right" | "center";
  /** Vertical alignment: top, bottom, or middle */
  valign?: "top" | "bottom" | "middle";
  /** Whether to apply device pixel ratio (default: true) */
  useDevicePixelRatio?: boolean;
}

interface ParsedImagorUrl {
  /** Protocol and domain (level 1) */
  domain: string;
  /** Security level: /unsafe or /HASH (level 2) */
  security: string;
  /** Image path (level 4) */
  imagePath: string;
}

export class ImagorService {
  /**
   * Gets device pixel ratio for high-DPI displays
   *
   * @returns Device pixel ratio (default: 1)
   */
  private getDevicePixelRatio(): number {
    if (typeof window !== "undefined" && window.devicePixelRatio) {
      return window.devicePixelRatio;
    }
    return 1;
  }

  /**
   * Transforms an imagor URL with new parameters
   *
   * @param imageUrl - Original imagor URL
   * @param options - Transformation options
   * @returns Transformed imagor URL
   */
  public transformUrl(
    imageUrl: string,
    options: ImagorTransformOptions = {}
  ): string {
    if (!imageUrl || typeof imageUrl !== "string") {
      console.warn("ImagorService: Invalid image URL provided");
      return imageUrl;
    }

    try {
      // Parse the URL into components
      const parsed = this.parseImagorUrl(imageUrl);
      if (!parsed) {
        return imageUrl;
      }

      // Apply device pixel ratio to dimensions if enabled (default: true)
      const useDPR = options.useDevicePixelRatio !== false;
      const dpr = useDPR ? this.getDevicePixelRatio() : 1;

      // Create adjusted options with DPR applied
      const adjustedOptions: ImagorTransformOptions = {
        ...options,
        width:
          options.width !== undefined
            ? Math.round(options.width * dpr)
            : undefined,
        height:
          options.height !== undefined
            ? Math.round(options.height * dpr)
            : undefined,
      };

      // Build new parameters from adjusted options
      const params = this.buildImagorParams(adjustedOptions);

      // Build and return the new URL
      return this.buildImagorUrl(parsed, params);
    } catch (error) {
      console.error("ImagorService: Error transforming URL", error);
      return imageUrl;
    }
  }

  /**
   * Parses an imagor URL into its components
   *
   * @param url - Imagor URL to parse
   * @returns Parsed URL components or null if invalid
   */
  private parseImagorUrl(url: string): ParsedImagorUrl | null {
    try {
      const urlObj = new URL(url);
      const domain = `${urlObj.protocol}//${urlObj.host}`;
      const pathname = urlObj.pathname;

      // Split pathname into parts
      const parts = pathname.split("/").filter((part) => part.length > 0);

      if (parts.length < 2) {
        console.warn("ImagorService: URL does not have enough path segments");
        return null;
      }

      // Level 2: security (first segment: /unsafe or /HASH)
      const security = `/${parts[0]}`;

      // Find where the image path starts (after security and parameters)
      // Image path typically starts after we find segments that don't match known parameters
      let imagePathStartIndex = 1;

      // Skip known parameter segments (fit-in, smart, alignments, size patterns)
      for (let i = 1; i < parts.length; i++) {
        const part = parts[i];

        // Check if this is a known parameter
        if (
          part === "fit-in" ||
          part === "smart" ||
          part === "trim" ||
          part === "stretch" ||
          part === "left" ||
          part === "right" ||
          part === "center" ||
          part === "top" ||
          part === "bottom" ||
          part === "middle" ||
          /^-?\d+x-?\d+/.test(part) || // Size pattern like "200x200" or "-200x-200"
          /^\d+:\d+:\d+:\d+/.test(part) || // Crop pattern like "10:20:200:150"
          /^filters:/.test(part) // Filter pattern like "filters:fill(white)"
        ) {
          imagePathStartIndex = i + 1;
        } else {
          // This looks like the start of the image path
          break;
        }
      }

      // Level 4: image path (everything from imagePathStartIndex onwards)
      const imagePath = `/${parts.slice(imagePathStartIndex).join("/")}`;

      return {
        domain,
        security,
        imagePath,
      };
    } catch (error) {
      console.error("ImagorService: Error parsing URL", error);
      return null;
    }
  }

  /**
   * Builds imagor parameters string from options
   *
   * @param options - Transformation options
   * @returns Parameters string (without leading slash)
   */
  private buildImagorParams(options: ImagorTransformOptions): string {
    const params: string[] = [];

    // Add fit-in if specified
    if (options.fitIn) {
      params.push("fit-in");
    }

    // Add size (width x height)
    if (options.width !== undefined || options.height !== undefined) {
      const width = options.width ?? 0;
      const height = options.height ?? 0;
      params.push(`${width}x${height}`);
    }

    // Add smart detection if specified
    if (options.smart) {
      params.push("smart");
    }

    // Add horizontal alignment if specified
    if (options.halign) {
      params.push(options.halign);
    }

    // Add vertical alignment if specified
    if (options.valign) {
      params.push(options.valign);
    }

    return params.join("/");
  }

  /**
   * Builds the final imagor URL from components and parameters
   *
   * @param parsed - Parsed URL components
   * @param params - Parameters string
   * @returns Complete imagor URL
   */
  private buildImagorUrl(parsed: ParsedImagorUrl, params: string): string {
    const parts = [
      parsed.domain,
      parsed.security,
      ...(params ? [params] : []),
      parsed.imagePath.replace(/^\//, ""), // Remove leading slash if present
    ].filter(Boolean);

    return parts.join("/");
  }
}

// Create and export singleton instance
export const imagorService = new ImagorService();
